import React, { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'react-toastify';
import { Plus, Trash, Wrench, Warning, FloppyDisk, ArrowCounterClockwise } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Card, CardContent } from '@/components/ui/card';
import { SimpleDropdown } from '@/components/ui/simple-dropdown';
import {
  MaintenanceFrequency,
  MaintenanceFrequencyLabels,
  MaintenanceType,
  MaintenanceTypeLabels,
  MaintenanceResponsibleLabels,
  MaintenanceScheduleTemplate,
  CreateMaintenanceTemplateDto,
} from 'shared/types/asset';
import {
  useMaintenanceTemplatesForType,
  useCreateMaintenanceTemplate,
  useUpdateMaintenanceTemplate,
  useDeleteMaintenanceTemplate,
  useAffectedAssetsCountForTemplate,
  useBulkApplyTemplate,
  useExistingSchedulesCount,
  usePropagateTemplateChanges,
} from 'hooks/useAssets';
import { useSegments } from 'hooks/useSegments';
import { BulkApplyMaintenanceDialog } from './BulkApplyMaintenanceDialog';
import { PropagateChangesDialog } from './PropagateChangesDialog';

// Fields that should be propagated to existing schedules
const PROPAGATABLE_FIELDS = [
  'name',
  'frequency',
  'customFrequencyMonths',
  'maintenanceType',
  'responsibleParty',
];

interface MaintenanceTemplateSectionProps {
  assetTypeId: number;
  onDirtyStateChange?: (isDirty: boolean) => void;
}

export const MaintenanceTemplateSection: React.FC<MaintenanceTemplateSectionProps> = ({
  assetTypeId,
  onDirtyStateChange,
}) => {
  const { templates, isLoading, refetch } = useMaintenanceTemplatesForType(assetTypeId);
  const { createTemplate } = useCreateMaintenanceTemplate();
  const { updateTemplate } = useUpdateMaintenanceTemplate();
  const { deleteTemplate } = useDeleteMaintenanceTemplate();
  const { segments } = useSegments();

  // Bulk apply (create/remove schedules for segment changes)
  const { fetchCount: fetchBulkCount } = useAffectedAssetsCountForTemplate();
  const { bulkApply, isApplying: isBulkApplying } = useBulkApplyTemplate();

  // Propagate field changes to existing schedules
  const { fetchCount: fetchExistingCount } = useExistingSchedulesCount();
  const { propagate, isPropagating } = usePropagateTemplateChanges();

  // Bulk apply dialog state
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [bulkDialogTemplateId, setBulkDialogTemplateId] = useState<number | null>(null);
  const [bulkDialogToCreate, setBulkDialogToCreate] = useState(0);
  const [bulkDialogToRemove, setBulkDialogToRemove] = useState(0);
  const [bulkDialogTemplateName, setBulkDialogTemplateName] = useState('');

  // Propagate dialog state
  const [propagateDialogOpen, setPropagateDialogOpen] = useState(false);
  const [propagateTemplateId, setPropagateTemplateId] = useState<number | null>(null);
  const [propagateTemplateName, setPropagateTemplateName] = useState('');
  const [propagateExistingCount, setPropagateExistingCount] = useState(0);
  const [propagateFields, setPropagateFields] = useState<Record<string, any>>({});

  // Pending bulk apply — queued when propagate dialog is shown first
  const pendingBulkApplyRef = useRef<{
    templateId: number;
    toCreate: number;
    toRemove: number;
    templateName: string;
  } | null>(null);

  // Track which templates have unsaved changes
  const [dirtyTemplateIds, setDirtyTemplateIds] = useState<Set<number>>(new Set());

  // Notify parent about dirty state changes
  const hasDirty = dirtyTemplateIds.size > 0;
  useEffect(() => {
    onDirtyStateChange?.(hasDirty);
  }, [hasDirty, onDirtyStateChange]);

  const markDirty = useCallback((templateId: number) => {
    setDirtyTemplateIds((prev) => new Set([...prev, templateId]));
  }, []);

  const markClean = useCallback((templateId: number) => {
    setDirtyTemplateIds((prev) => {
      const next = new Set(prev);
      next.delete(templateId);
      return next;
    });
  }, []);

  const handleAddTemplate = async () => {
    try {
      const dto: CreateMaintenanceTemplateDto = {
        name: '',
        frequency: MaintenanceFrequency.YEARLY,
        maintenanceType: MaintenanceType.PREVENTIVE,
      };
      await createTemplate(assetTypeId, dto);
      await refetch();
      toast.success('Onderhoudsregel toegevoegd');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Fout bij toevoegen';
      toast.error(msg);
    }
  };

  const handleSaveTemplate = async (
    template: MaintenanceScheduleTemplate,
    localFields: LocalTemplateFields,
  ) => {
    // Build updates object with all changed fields
    const updates: Record<string, any> = {};
    if (localFields.name !== template.name) updates.name = localFields.name;
    if (localFields.frequency !== template.frequency) updates.frequency = localFields.frequency;
    if ((localFields.customFrequencyMonths ?? null) !== (template.customFrequencyMonths ?? null))
      updates.customFrequencyMonths = localFields.customFrequencyMonths;
    if (localFields.maintenanceType !== template.maintenanceType) updates.maintenanceType = localFields.maintenanceType;
    if ((localFields.responsibleParty ?? null) !== (template.responsibleParty ?? null))
      updates.responsibleParty = localFields.responsibleParty;
    if ((localFields.segmentId ?? null) !== (template.segmentId ?? null)) {
      updates.segmentId = localFields.segmentId;
      updates.conditions = null; // clear legacy
    }
    if (localFields.active !== template.active) updates.active = localFields.active;

    if (Object.keys(updates).length === 0) {
      markClean(template.id);
      return;
    }

    try {
      await updateTemplate(template.id, updates);
      await refetch();
      markClean(template.id);
      toast.success('Onderhoudsregel opgeslagen');

      const updateKeys = Object.keys(updates);
      const hasPropagatable = updateKeys.some((k) => PROPAGATABLE_FIELDS.includes(k));
      const templateName = localFields.name || template.name || 'Naamloos';
      const isDeactivating = 'active' in updates && updates.active === false;

      console.log('[MaintenanceTemplate] Saved template', template.id, '— updates:', updateKeys, '| hasPropagatable:', hasPropagatable, '| isDeactivating:', isDeactivating);

      // Always check if there are assets missing schedules (unless template was deactivated)
      let bulkApplyData: { toCreate: number; toRemove: number } | null = null;
      if (!isDeactivating) {
        try {
          const result = await fetchBulkCount(template.id);
          console.log('[MaintenanceTemplate] Bulk apply count for', template.id, ':', result);
          if (result.toCreate > 0 || result.toRemove > 0) {
            bulkApplyData = result;
          }
        } catch (countErr) {
          console.error('[MaintenanceTemplate] Failed to fetch affected assets count:', countErr);
          toast.error('Fout bij ophalen van betrokken voorzieningen');
        }
      }

      // 1. Check if propagatable field changes should go to existing schedules
      if (hasPropagatable) {
        try {
          const isNameChange = 'name' in updates;
          const oldName = isNameChange ? template.name : undefined;
          const { count } = await fetchExistingCount(template.id, oldName);
          console.log('[MaintenanceTemplate] Existing schedules count for', template.id, ':', count, '(oldName:', oldName, ')');
          if (count > 0) {
            const fieldsToPropagate: Record<string, any> = {};
            for (const key of updateKeys) {
              if (PROPAGATABLE_FIELDS.includes(key)) {
                fieldsToPropagate[key] = updates[key];
              }
            }
            if (isNameChange) {
              fieldsToPropagate.previousName = template.name;
            }

            // Queue bulk apply for after propagate dialog if needed
            if (bulkApplyData) {
              pendingBulkApplyRef.current = {
                templateId: template.id,
                toCreate: bulkApplyData.toCreate,
                toRemove: bulkApplyData.toRemove,
                templateName,
              };
            }

            setPropagateTemplateId(template.id);
            setPropagateTemplateName(templateName);
            setPropagateExistingCount(count);
            setPropagateFields(fieldsToPropagate);
            setPropagateDialogOpen(true);
            return;
          }
        } catch (countErr) {
          console.error('[MaintenanceTemplate] Failed to fetch existing schedules count:', countErr);
          toast.error('Fout bij ophalen van bestaande schema\'s');
        }
      }

      // 2. Show bulk apply dialog if there are assets needing schedules
      if (bulkApplyData) {
        setBulkDialogTemplateId(template.id);
        setBulkDialogToCreate(bulkApplyData.toCreate);
        setBulkDialogToRemove(bulkApplyData.toRemove);
        setBulkDialogTemplateName(templateName);
        setBulkDialogOpen(true);
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Fout bij opslaan';
      toast.error(msg);
    }
  };

  const showPendingBulkApply = useCallback(() => {
    const pending = pendingBulkApplyRef.current;
    if (pending) {
      pendingBulkApplyRef.current = null;
      setBulkDialogTemplateId(pending.templateId);
      setBulkDialogToCreate(pending.toCreate);
      setBulkDialogToRemove(pending.toRemove);
      setBulkDialogTemplateName(pending.templateName);
      setBulkDialogOpen(true);
    }
  }, []);

  const handlePropagateConfirm = async () => {
    if (!propagateTemplateId) return;
    try {
      const { updated } = await propagate(propagateTemplateId, propagateFields);
      toast.success(`${updated} onderhoudsschema's bijgewerkt`);
      setPropagateDialogOpen(false);
      showPendingBulkApply();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Fout bij toepassen';
      toast.error(msg);
    }
  };

  const handlePropagateSkip = useCallback(() => {
    setPropagateDialogOpen(false);
    showPendingBulkApply();
  }, [showPendingBulkApply]);

  const handleBulkApplyConfirm = async (nextDueDate: string) => {
    if (!bulkDialogTemplateId) return;
    try {
      const { created, removed } = await bulkApply(bulkDialogTemplateId, nextDueDate);
      const parts: string[] = [];
      if (removed > 0) parts.push(`${removed} verwijderd`);
      if (created > 0) parts.push(`${created} aangemaakt`);
      toast.success(`Onderhoudsschema's: ${parts.join(', ')}`);
      setBulkDialogOpen(false);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Fout bij toepassen';
      toast.error(msg);
    }
  };

  const handleDeleteTemplate = async (templateId: number) => {
    try {
      await deleteTemplate(templateId);
      markClean(templateId);
      await refetch();
      toast.success('Onderhoudsregel verwijderd');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Fout bij verwijderen';
      toast.error(msg);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Wrench className="h-4 w-4 text-muted-foreground" />
          <h4 className="text-sm font-semibold">Standaard onderhoud</h4>
          <Badge variant="secondary" className="text-xs">
            {templates.length}
          </Badge>
        </div>
        <Button variant="outline" size="sm" onClick={handleAddTemplate}>
          <Plus className="h-3 w-3 mr-1" />
          Onderhoudsregel toevoegen
        </Button>
      </div>

      {isLoading && <p className="text-sm text-muted-foreground">Laden...</p>}

      {!isLoading && templates.length === 0 && (
        <p className="text-sm text-muted-foreground">
          Geen standaard onderhoudsregels. Voeg een regel toe om automatisch onderhoudsschema&apos;s
          aan te maken bij nieuwe voorzieningen van dit type.
        </p>
      )}

      <div className="space-y-2">
        {templates.map((template) => (
          <TemplateCard
            key={template.id}
            template={template}
            segments={segments}
            onSave={handleSaveTemplate}
            onDelete={handleDeleteTemplate}
            onDirtyChange={(isDirty) =>
              isDirty ? markDirty(template.id) : markClean(template.id)
            }
          />
        ))}
      </div>

      {/* Propagate field changes to existing schedules */}
      <PropagateChangesDialog
        open={propagateDialogOpen}
        onOpenChange={setPropagateDialogOpen}
        existingCount={propagateExistingCount}
        templateName={propagateTemplateName}
        onConfirm={handlePropagateConfirm}
        onSkip={handlePropagateSkip}
        isPropagating={isPropagating}
      />

      {/* Bulk create/remove schedules */}
      <BulkApplyMaintenanceDialog
        open={bulkDialogOpen}
        onOpenChange={setBulkDialogOpen}
        toCreateCount={bulkDialogToCreate}
        toRemoveCount={bulkDialogToRemove}
        templateName={bulkDialogTemplateName}
        onConfirm={handleBulkApplyConfirm}
        onSkip={() => setBulkDialogOpen(false)}
        isApplying={isBulkApplying}
      />
    </div>
  );
};

// ─── Local field state for a template card ───────────────────────────

interface LocalTemplateFields {
  name: string;
  frequency: string;
  customFrequencyMonths?: number | null;
  maintenanceType: string;
  responsibleParty?: string | null;
  segmentId?: number | null;
  active: boolean;
}

function getLocalFields(t: MaintenanceScheduleTemplate): LocalTemplateFields {
  return {
    name: t.name || '',
    frequency: t.frequency,
    customFrequencyMonths: t.customFrequencyMonths ?? null,
    maintenanceType: t.maintenanceType,
    responsibleParty: t.responsibleParty ?? null,
    segmentId: t.segmentId ?? null,
    active: t.active,
  };
}

function fieldsChanged(
  local: LocalTemplateFields,
  server: MaintenanceScheduleTemplate,
): boolean {
  return (
    local.name !== (server.name || '') ||
    local.frequency !== server.frequency ||
    (local.customFrequencyMonths ?? null) !== (server.customFrequencyMonths ?? null) ||
    local.maintenanceType !== server.maintenanceType ||
    (local.responsibleParty ?? null) !== (server.responsibleParty ?? null) ||
    (local.segmentId ?? null) !== (server.segmentId ?? null) ||
    local.active !== server.active
  );
}

// ─── Individual Template Card ────────────────────────────────────────

interface SegmentOption {
  id: number;
  name: string;
}

interface TemplateCardProps {
  template: MaintenanceScheduleTemplate;
  segments: SegmentOption[];
  onSave: (template: MaintenanceScheduleTemplate, localFields: LocalTemplateFields) => Promise<void>;
  onDelete: (id: number) => void;
  onDirtyChange: (isDirty: boolean) => void;
}

const TemplateCard: React.FC<TemplateCardProps> = ({
  template,
  segments,
  onSave,
  onDelete,
  onDirtyChange,
}) => {
  const [local, setLocal] = useState<LocalTemplateFields>(() => getLocalFields(template));
  const prevTemplateRef = useRef(template);

  // Sync local state when server data changes (after save + refetch)
  useEffect(() => {
    // Only sync if the server template actually changed (new data from refetch)
    if (prevTemplateRef.current !== template) {
      setLocal(getLocalFields(template));
      prevTemplateRef.current = template;
    }
  }, [template]);

  const hasChanges = fieldsChanged(local, template);

  // Notify parent about dirty state
  useEffect(() => {
    onDirtyChange(hasChanges);
  }, [hasChanges, onDirtyChange]);

  const update = (field: keyof LocalTemplateFields, value: any) => {
    setLocal((prev) => ({ ...prev, [field]: value }));
  };

  const handleDiscard = () => {
    setLocal(getLocalFields(template));
  };

  const [isSaving, setIsSaving] = useState(false);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await onSave(template, local);
    } catch (err) {
      console.error('[TemplateCard] Save failed:', err);
    } finally {
      setIsSaving(false);
    }
  };

  const segmentOptions = [
    { value: '', label: 'Geen (geldt overal)' },
    ...segments.map((s) => ({ value: String(s.id), label: s.name })),
  ];

  return (
    <Card className={`border ${hasChanges ? 'border-amber-400 shadow-sm shadow-amber-100' : ''}`}>
      <CardContent className="p-3 space-y-3">
        {/* Row 1: Name + Active + Delete */}
        <div className="flex items-center gap-2">
          <div className="flex-1">
            <Input
              value={local.name}
              onChange={(e) => update('name', e.target.value)}
              placeholder="Naam onderhoudsregel"
              className="h-8 text-sm"
            />
          </div>
          <div className="flex items-center gap-2">
            <Label className="text-xs text-muted-foreground">Actief</Label>
            <Switch
              checked={local.active}
              onCheckedChange={(checked) => update('active', checked)}
            />
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 text-destructive"
            onClick={() => onDelete(template.id)}
          >
            <Trash className="h-3.5 w-3.5" />
          </Button>
        </div>

        {/* Row 2: Frequency + Type + Responsible */}
        <div className="grid grid-cols-4 gap-2">
          <div>
            <Label className="text-xs mb-1 block">Frequentie</Label>
            <Select
              value={local.frequency}
              onValueChange={(val) => update('frequency', val)}
            >
              <SelectTrigger className="h-8 text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent position="popper" sideOffset={4} align="start" className="max-h-[200px]" {...{ modal: false } as any}>
                {Object.entries(MaintenanceFrequencyLabels).map(([key, label]) => (
                  <SelectItem key={key} value={key}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {local.frequency === MaintenanceFrequency.CUSTOM && (
            <div>
              <Label className="text-xs mb-1 block">Maanden</Label>
              <Input
                type="number"
                min={1}
                value={local.customFrequencyMonths || ''}
                onChange={(e) =>
                  update('customFrequencyMonths', parseInt(e.target.value) || null)
                }
                placeholder="Aantal"
                className="h-8 text-sm"
              />
            </div>
          )}

          <div>
            <Label className="text-xs mb-1 block">Type</Label>
            <Select
              value={local.maintenanceType}
              onValueChange={(val) => update('maintenanceType', val)}
            >
              <SelectTrigger className="h-8 text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent position="popper" sideOffset={4} align="start" className="max-h-[200px]" {...{ modal: false } as any}>
                {Object.entries(MaintenanceTypeLabels).map(([key, label]) => (
                  <SelectItem key={key} value={key}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-xs mb-1 block">Verantwoordelijke</Label>
            <Select
              value={local.responsibleParty || ''}
              onValueChange={(val) => update('responsibleParty', val || null)}
            >
              <SelectTrigger className="h-8 text-sm">
                <SelectValue placeholder="Geen" />
              </SelectTrigger>
              <SelectContent position="popper" sideOffset={4} align="start" className="max-h-[200px]" {...{ modal: false } as any}>
                {Object.entries(MaintenanceResponsibleLabels).map(([key, label]) => (
                  <SelectItem key={key} value={key}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Warning: vacant units not considered */}
        {local.responsibleParty && (
          <div className="flex items-start gap-1.5 rounded-md bg-amber-50 border border-amber-200 p-2">
            <Warning className="h-3.5 w-3.5 text-amber-600 mt-0.5 shrink-0" />
            <span className="text-xs text-amber-700">
              Let op: momenteel houdt het systeem geen rekening met leegstaande units/gebouwen om de verplichting te bepalen.
            </span>
          </div>
        )}

        {/* Row 3: Segment selection */}
        <div>
          <Label className="text-xs mb-1.5 block text-muted-foreground">
            Segment (leeg = geldt overal)
          </Label>
          <SimpleDropdown
            value={local.segmentId ? String(local.segmentId) : ''}
            options={segmentOptions}
            onChange={(val) => update('segmentId', val ? Number(val) : null)}
            placeholder="Geen (geldt overal)"
            triggerClassName="h-8 text-sm"
          />
        </div>

        {/* Save / Discard bar — visible when there are unsaved changes */}
        {hasChanges && (
          <div className="flex items-center justify-between pt-1 border-t border-amber-200">
            <span className="text-xs text-amber-600 font-medium">
              Niet-opgeslagen wijzigingen
            </span>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                className="h-7 text-xs"
                onClick={handleDiscard}
              >
                <ArrowCounterClockwise className="h-3 w-3 mr-1" />
                Ongedaan maken
              </Button>
              <Button
                size="sm"
                className="h-7 text-xs bg-brick-primary hover:bg-brick-primary/90 text-white"
                onClick={handleSave}
                disabled={isSaving}
              >
                <FloppyDisk className="h-3 w-3 mr-1" />
                Opslaan
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
